import { createContext, useState } from 'react'

import {
  connect,
  useData,
  Obj,
  Widget,
  ContentTag,
  currentPageParams,
} from 'scrivito'

import { parseOrder } from '@/Components/EntityTable/orderParam'
import {
  getListConfig,
  CURRENT_USER,
  type ListConfig,
} from '@/config/listConfig'
import { DataClassName } from '@/Data/const'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import { ensureString } from '@/utils/ensureString'
import { paramsToFilters } from '@/utils/filterParams'
import { isString } from '@/utils/isString'

/**
 * The List "Object" filter key (see `ObjectFilter.tsx`). PISA's `/list`
 * endpoint 422s ("The given field 'entityType' is invalid") when this
 * attribute is sent as a server-side filter, even though it's readable per
 * item — so it's applied client-side in `DXPOverviewWidgetComponent` instead.
 */
const LIST_ENTITY_TYPE_FILTER_KEY = 'entityType'

export const EntityListContext = createContext<{
  hasMore: () => boolean
  loadMore: () => void
}>({
  hasMore: () => true,
  loadMore: () => {
    throw new Error('loadMore not provided')
  },
})

const resolveScope = (
  scope: ListConfig['scope'],
  contactId: string | null,
): Record<string, string | boolean> => {
  if (!scope) {
    return {}
  }
  const out: Record<string, string | boolean> = {}
  for (const [k, v] of Object.entries(scope)) {
    if (v === CURRENT_USER) {
      // If the runtime value isn't ready, omit the key — never send contact:null,
      // that would broaden the query to all users.
      if (contactId) {
        out[k] = contactId
      }
    } else {
      out[k] = v
    }
  }
  return out
}

export const EntityListContextProvider = connect(
  function EntityListContextProvider({
    attribute,
    content,
    id,
    tag,
  }: {
    attribute: string
    content: Obj | Widget
    id?: string
    tag?: string
  }) {
    const params = currentPageParams()
    const search = ensureString(params['search'] ?? null)
    const filter = ensureString(params['filter'] ?? null)
    const query = ensureString(params['query'] ?? null)
    const labels = ensureString(params['labels'] ?? null)

    const dataScope = useData()
    const listConfig = getListConfig(dataScope.dataClassName() ?? '')

    const attributeFilters = paramsToFilters(params)
    if (dataScope.dataClassName() === DataClassName.LIST) {
      delete attributeFilters[LIST_ENTITY_TYPE_FILTER_KEY]
    }

    // Default order feeds the fetch transform only. Writing it to the URL is the
    // job of `useTableSorter`, which mounts solely inside `EntityTable` (real
    // list pages) — so the dashboard and single-entity detail pages, which this
    // provider also wraps, never get a meaningless `order` param.
    const orderParam = parseOrder(ensureString(params['order'] ?? null))
    const order = orderParam ?? listConfig.defaultOrder

    const contactId = WhoAmI.get('contactId') as string | null
    const scope = resolveScope(listConfig.scope, contactId)

    const searchKey = listConfig.searchKey ?? ''

    const configuredLimit = dataScope.limit() ?? 20
    const [limit, setLimit] = useState(configuredLimit)
    const [initialLimit, setInitialLimit] = useState(configuredLimit)

    if (initialLimit !== configuredLimit) {
      setInitialLimit(configuredLimit)
      setLimit(configuredLimit)
    }

    const filters: Record<string, string | boolean | null> = {
      ...scope,
      ...attributeFilters,
    }

    // `filter` and `query` are mutually exclusive: a selected saved filter wins,
    // and the unsaved draft only drives the list when none is selected. Sending
    // both would AND them together. The draft stays in the URL either way so the
    // user can switch back to it — see `useActiveFilter`.
    if (isString(filter)) {
      filters.filter = filter
    } else if (query) {
      filters.query = query
    }
    if (isString(labels)) {
      filters.labels = labels
    }
    if (searchKey && search) {
      filters[searchKey] = search
    }

    const transform = {
      limit,
      search: !searchKey ? search : undefined,
      filters,
      order: order ? [order] : undefined,
    }
    const hasMore = () => {
      try {
        return (
          dataScope.transform({ ...transform, limit: limit + 1 }).take()
            .length > limit
        )
      } catch {
        return false
      }
    }

    const loadMore = () => setLimit((prevLimit) => prevLimit + configuredLimit)

    return (
      <EntityListContext.Provider value={{ hasMore, loadMore }}>
        <ContentTag
          tag={tag}
          id={id}
          content={content}
          attribute={attribute}
          dataContext={dataScope.transform(transform)}
        />
      </EntityListContext.Provider>
    )
  },
)
